import { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { getFeedback, markFeedbackRead, feedbackPosterPdfUrl, deleteFeedbackMany, deleteAllFeedback } from "@/api";
import { Loading } from "./Layout";
import { Star, MessageSquare, ChevronDown, QrCode, Download, Check, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { toast } from "sonner";

const fmtAvg = (a) => (a == null ? "–" : a.toFixed(1).replace(".", ","));

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch (e) { return ""; }
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} von 5 Sternen`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className="h-4 w-4" style={{ color: n <= value ? "#F59E0B" : "#e2e8f0" }} fill={n <= value ? "#F59E0B" : "none"} strokeWidth={n <= value ? 1.5 : 2} />
    ))}
  </span>
);

const SummaryStat = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex-1 bg-white rounded-2xl ring-1 ring-slate-100 shadow-soft px-4 py-3.5 text-center">
    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-display font-extrabold text-2xl text-[var(--bs-ink)] mt-1">{value}</p>
  </div>
);

const ShareSection = ({ business }) => {
  const [showQr, setShowQr] = useState(false);
  const url = `${window.location.origin}/feedback/${business?.slug}`;
  return (
    <div data-testid="feedback-share" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6">
      <h2 className="font-display font-extrabold text-lg text-[var(--bs-ink)]">Feedback teilen</h2>
      <p className="text-sm text-slate-500 leading-relaxed mt-1">
        Nutze den QR-Code oder einen Aushang, damit Kundinnen und Kunden anonym Feedback geben können.
      </p>
      <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {showQr && (
          <div data-testid="feedback-qr" className="p-3 rounded-2xl bg-white ring-1 ring-slate-100 shadow-sm shrink-0">
            <QRCodeSVG value={url} size={148} fgColor="#000000" bgColor="#FFFFFF" level="M" />
          </div>
        )}
        <div className="flex-1 w-full space-y-2.5">
          <button
            data-testid="feedback-toggle-qr"
            onClick={() => setShowQr((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 press"
          >
            <QrCode className="h-4 w-4" /> {showQr ? "QR-Code ausblenden" : "QR-Code anzeigen"}
          </button>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              data-testid="feedback-download-a4"
              onClick={() => window.open(feedbackPosterPdfUrl(business.slug, "a4"), "_blank")}
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] text-white font-display font-bold press"
            >
              <Download className="h-4 w-4" /> A4-Aushang herunterladen
            </button>
            <button
              data-testid="feedback-download-a5"
              onClick={() => window.open(feedbackPosterPdfUrl(business.slug, "a5"), "_blank")}
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-display font-bold press"
            >
              <Download className="h-4 w-4" /> A5-Aushang herunterladen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeedbackTab = ({ business }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmSel, setConfirmSel] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { setData(await getFeedback()); }, []);
  useEffect(() => { load(); }, [load]);

  const exitSelect = () => { setSelecting(false); setSelected(new Set()); };

  const openEntry = async (item) => {
    const next = expanded === item.id ? null : item.id;
    setExpanded(next);
    if (next && !item.is_read) {
      setData((d) => ({
        ...d,
        unread: Math.max(0, d.unread - 1),
        items: d.items.map((f) => (f.id === item.id ? { ...f, is_read: true } : f)),
      }));
      try { await markFeedbackRead(item.id); } catch (e) { load(); }
    }
  };

  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const doDeleteSelected = async () => {
    setBusy(true);
    try {
      const res = await deleteFeedbackMany([...selected]);
      toast.success(`${res.deleted} gelöscht`);
      exitSelect();
      await load();
    } catch (e) { toast.error("Löschen fehlgeschlagen"); }
    finally { setBusy(false); setConfirmSel(false); }
  };

  const doDeleteAll = async () => {
    setBusy(true);
    try {
      const res = await deleteAllFeedback();
      toast.success(`${res.deleted} gelöscht`);
      exitSelect();
      await load();
    } catch (e) { toast.error("Löschen fehlgeschlagen"); }
    finally { setBusy(false); setConfirmAll(false); }
  };

  if (!data) return <Loading />;

  const { average, total, unread, items } = data;
  const allSelected = items.length > 0 && selected.size === items.length;
  const selCount = selected.size;
  const delLabel = selCount === 1 ? "1 Rückmeldung löschen" : `${selCount} Rückmeldungen löschen`;

  return (
    <div data-testid="feedback-tab" className="max-w-2xl mx-auto space-y-4">
      <ShareSection business={business} />
      {total === 0 ? (
        <div data-testid="feedback-empty" className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
            <MessageSquare className="h-7 w-7 text-slate-400" />
          </div>
          <h2 className="font-display font-extrabold text-xl text-[var(--bs-ink)]">Noch kein Feedback</h2>
          <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed max-w-sm mx-auto">
            Neue Rückmeldungen erscheinen hier, sobald Kundinnen und Kunden Feedback abgegeben haben.
          </p>
        </div>
      ) : (
        <>
          <div className="flex gap-3">
            <SummaryStat testid="feedback-stat-average" label="Durchschnitt" value={<span className="inline-flex items-center gap-1">{fmtAvg(average)} <Star className="h-5 w-5" style={{ color: "#F59E0B" }} fill="#F59E0B" strokeWidth={1.5} /></span>} />
            <SummaryStat testid="feedback-stat-total" label="Rückmeldungen" value={total} />
            <SummaryStat testid="feedback-stat-unread" label="Ungelesen" value={unread} />
          </div>

          {/* Selection toolbar */}
          <div className="flex items-center justify-between gap-3 px-1">
            {!selecting ? (
              <button data-testid="feedback-select-toggle" onClick={() => setSelecting(true)}
                className="text-sm font-bold text-slate-500 hover:text-slate-700 press">
                Auswählen
              </button>
            ) : (
              <div className="flex items-center gap-4">
                <button data-testid="feedback-select-cancel" onClick={exitSelect}
                  className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-slate-700 press">
                  <X className="h-4 w-4" /> Abbrechen
                </button>
                <button data-testid="feedback-select-all"
                  onClick={() => setSelected(allSelected ? new Set() : new Set(items.map((f) => f.id)))}
                  className="text-sm font-bold text-[var(--bs-teal)] hover:opacity-80 press">
                  {allSelected ? "Auswahl aufheben" : "Alle auswählen"}
                </button>
              </div>
            )}
            {selecting && (
              <button data-testid="feedback-delete-all" onClick={() => setConfirmAll(true)}
                className="text-sm font-semibold text-rose-500 hover:text-rose-600 press">
                Alle Feedbacks löschen
              </button>
            )}
          </div>

          {selecting && selCount > 0 && (
            <button data-testid="feedback-delete-selected" onClick={() => setConfirmSel(true)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-display font-bold press">
              <Trash2 className="h-4 w-4" /> {delLabel}
            </button>
          )}

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft divide-y divide-slate-100 overflow-hidden">
            {items.map((f) => {
              const isOpen = expanded === f.id;
              const isSel = selected.has(f.id);
              const onClick = () => (selecting ? toggleOne(f.id) : openEntry(f));
              return (
                <button
                  key={f.id}
                  data-testid="feedback-item"
                  data-read={f.is_read}
                  data-selected={isSel}
                  onClick={onClick}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50/60 press flex items-start gap-3"
                >
                  {selecting && (
                    <span data-testid="feedback-item-checkbox" className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center shrink-0 ring-1 ${isSel ? "bg-[var(--bs-teal)] ring-transparent" : "bg-white ring-slate-300"}`}>
                      {isSel && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                  )}
                  {!selecting && !f.is_read && <span data-testid="feedback-unread-dot" className="mt-1.5 h-2.5 w-2.5 rounded-full bg-[var(--bs-teal)] shrink-0" />}
                  <div className={`flex-1 min-w-0 ${!selecting && f.is_read ? "pl-[22px]" : ""}`}>
                    <div className="flex items-center justify-between gap-3">
                      <Stars value={f.rating} />
                      <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap flex items-center gap-1">
                        {fmtDateTime(f.created_at)}
                        {!selecting && f.improvement_comment && <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                      </span>
                    </div>
                    {f.improvement_comment && (
                      <p className={`text-sm text-slate-600 mt-1.5 leading-relaxed ${isOpen && !selecting ? "" : "line-clamp-2"}`}>
                        {f.improvement_comment}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Confirm: delete selected */}
      <AlertDialog open={confirmSel} onOpenChange={setConfirmSel}>
        <AlertDialogContent data-testid="feedback-confirm-selected">
          <AlertDialogHeader>
            <AlertDialogTitle>Ausgewählte Rückmeldungen löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die ausgewählten Rückmeldungen werden dauerhaft gelöscht und können nicht wiederhergestellt werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="feedback-confirm-selected-btn" disabled={busy} onClick={doDeleteSelected}
              className="bg-rose-600 hover:bg-rose-700">Endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm: delete all */}
      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent data-testid="feedback-confirm-all">
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Rückmeldungen löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle Feedbacks dieses Betriebs werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="feedback-confirm-all-btn" disabled={busy} onClick={doDeleteAll}
              className="bg-rose-600 hover:bg-rose-700">Alle endgültig löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
